import { useEffect, useMemo, useState } from 'react';
import { Answer, type Proposal, type Turn } from '../components/AskAi.tsx';
import { Icon } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { compact, dateTime, monthName } from '../lib/format.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useAction } from './manage/common.tsx';
import { Kpi } from './Reports.tsx';

/**
 * AI activity, for the owner: how much ERP PRO AI the business uses (questions and tokens, by
 * month and by person) and every conversation anyone had with it, with the changes it prepared
 * and who approved them. Each conversation the owner opens is recorded.
 */

interface Month {
  month: string;
  questions: number;
  people: number;
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
}
interface Usage {
  months: Month[];
  people: { id: string; name: string; questions: number; tokens: number; last_at: string | null }[];
  limit: number | null;
}
interface ChatRow {
  id: string;
  title: string;
  agent: string;
  archived: boolean;
  created_at: string;
  last_at: string | null;
  person: string | null;
  person_name: string | null;
  questions: number;
  proposals: number;
  applied: number;
}
interface Review {
  chat: { id: string; title: string; agent: string; archived: boolean; person_name: string | null };
  turns: { id: string; role: 'user' | 'assistant'; text: string; created_at: string; tools: { action: string; ok: boolean; label: string }[] | null }[];
  proposals: (Proposal & { decided_at: string | null; decided_by_name: string | null })[];
}

const tokensOf = (m: Month) => m.input + m.output + m.cache_read + m.cache_write;
const agentName = (key: string) => {
  const k = `ai.agent_${key}` as MessageKey;
  const text = t(k);
  return text === k ? key : text;
};

export function AiActivity() {
  const usage = useAction<Usage>('ai.usage', { months: 6 });
  const [person, setPerson] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const chats = useAction<{ chats: ChatRow[] }>('ai.all_chats', { person, q: search || null });
  const [open, setOpen] = useState<string | null>(null);
  const now = usage.data?.months[0];
  const max = Math.max(1, ...(usage.data?.months.map((m) => m.questions) ?? [1]));
  const who = usage.data?.people.find((p) => p.id === person)?.name;

  return (
    <Shell>
      <div className="page reports ai-act">
        <div className="page-head">
          <div>
            <h1>{t('aia.title')}</h1>
            <p className="muted">{t('aia.subtitle')}</p>
          </div>
        </div>
        {!usage.data || !now ? (
          <div className="ins-loading">
            <Skeleton h={110} r="var(--radius-lg)" />
            <Skeleton h={240} r="var(--radius-lg)" />
          </div>
        ) : (
          <>
            <div className="rep-kpis">
              <Kpi
                label={t('aia.questions_month')}
                value={usage.data.limit === null ? String(now.questions) : t('aia.of', { n: now.questions, max: usage.data.limit })}
                hint={usage.data.limit === null ? t('aia.no_limit') : t('aia.left', { n: Math.max(usage.data.limit - now.questions, 0) })}
                warn={usage.data.limit !== null && now.questions >= usage.data.limit * 0.8}
              />
              <Kpi label={t('aia.people_month')} value={String(now.people)} hint={t('aia.people_hint')} />
              <Kpi label={t('aia.tokens_month')} value={compact(tokensOf(now))} hint={t('aia.tokens_hint', { n: compact(now.cache_read) })} />
            </div>
            <div className="ai-act-grid">
              <section className="card ai-act-card" aria-labelledby="aia-months">
                <h2 id="aia-months" className="ai-act-h">
                  <Icon name="chart" size={18} /> {t('aia.by_month')}
                </h2>
                <ul className="ai-act-months">
                  {usage.data.months.map((m) => (
                    <li key={m.month}>
                      <span className="ai-act-month">{monthName(m.month)}</span>
                      <span className="ai-act-bar" aria-hidden="true">
                        <span style={{ width: `${Math.round((m.questions / max) * 100)}%` }} />
                      </span>
                      <strong className="num">{m.questions}</strong>
                      <span className="muted small num">{compact(tokensOf(m))}</span>
                    </li>
                  ))}
                </ul>
              </section>
              <section className="card ai-act-card" aria-labelledby="aia-people">
                <h2 id="aia-people" className="ai-act-h">
                  <Icon name="users" size={18} /> {t('aia.by_person')}
                </h2>
                {usage.data.people.length ? (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('aia.person')}</th>
                        <th className="num">{t('aia.questions')}</th>
                        <th className="num">{t('aia.tokens')}</th>
                        <th>{t('aia.last')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usage.data.people.map((p) => (
                        <tr key={p.id} className={person === p.id ? 'selected' : ''}>
                          <td>
                            <button type="button" className="link-button" onClick={() => setPerson(person === p.id ? null : p.id)} aria-pressed={person === p.id}>
                              {p.name}
                            </button>
                          </td>
                          <td className="num">{p.questions}</td>
                          <td className="num">{compact(p.tokens)}</td>
                          <td className="muted small">{p.last_at ? dateTime(p.last_at) : '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="muted">{t('aia.nobody')}</p>
                )}
              </section>
            </div>
          </>
        )}

        <section className="card ai-act-card" aria-labelledby="aia-chats">
          <div className="ai-act-chats-head">
            <h2 id="aia-chats" className="ai-act-h">
              <Icon name="messages" size={18} /> {t('aia.conversations')}
            </h2>
            <form
              className="ai-act-search"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                setSearch(q.trim());
              }}
            >
              <input className="input" type="search" value={q} onChange={(e) => (setQ(e.target.value), e.target.value ? null : setSearch(''))} placeholder={t('aia.search')} aria-label={t('aia.search')} />
              <Button type="submit" icon="search">
                {t('aia.find')}
              </Button>
            </form>
          </div>
          {who ? (
            <p className="ai-act-filter">
              <span className="tag">
                {t('aia.only', { name: who })}
                <button type="button" className="tag-x" aria-label={t('aia.show_everyone')} onClick={() => setPerson(null)}>
                  <Icon name="x" size={12} />
                </button>
              </span>
            </p>
          ) : null}
          {!chats.data ? (
            <Skeleton h={180} r="var(--radius-md)" />
          ) : !chats.data.chats.length ? (
            <EmptyState icon="sparkle" title={search || person ? t('aia.none_found') : t('aia.none')} body={search || person ? t('aia.none_found_body') : t('aia.none_body')} />
          ) : (
            <ul className="ai-act-list">
              {chats.data.chats.map((c) => (
                <li key={c.id}>
                  <button type="button" className="ai-act-row" onClick={() => setOpen(c.id)}>
                    <span className="ai-act-row-main">
                      <strong>{c.title}</strong>
                      <span className="muted small">
                        {c.person_name ?? '–'} · {agentName(c.agent)} · {tn('aia.n_questions', c.questions)}
                        {c.archived ? ` · ${t('aia.archived')}` : ''}
                      </span>
                    </span>
                    {c.proposals ? <StatusBadge tone={c.applied ? 'ok' : 'neutral'} icon="checkCircle" label={t('aia.changes', { n: c.applied, total: c.proposals })} /> : null}
                    <time className="muted small" dateTime={c.last_at ?? c.created_at}>
                      {dateTime(c.last_at ?? c.created_at)}
                    </time>
                    <Icon name="chevronRight" size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <p className="muted small ai-act-note">
          <Icon name="shieldCheck" size={14} /> {t('aia.recorded')}
        </p>
      </div>
      {open ? <Transcript id={open} onClose={() => setOpen(null)} /> : null}
    </Shell>
  );
}

/** One conversation as the person saw it, read-only, with the changes and who decided them. */
function Transcript({ id, onClose }: { id: string; onClose: () => void }) {
  const { fail } = useUi();
  const [data, setData] = useState<Review | null>(null);
  useEffect(() => {
    let live = true;
    action<Review>('ai.review_chat', { chat: id }).then(
      (r) => live && setData(r),
      (err) => {
        fail(err);
        onClose();
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const turns = useMemo(() => {
    if (!data) return [];
    const list: Turn[] = data.turns.map((x) => ({ id: x.id, role: x.role, text: x.text, created_at: x.created_at, steps: (x.tools ?? []).map((s) => ({ action: s.action, label: s.label, done: true, ok: s.ok })) }));
    for (const prop of data.proposals) {
      const at = list.find((x) => x.role === 'assistant' && (x.created_at ?? '') >= (prop.created_at ?? '')) ?? list.filter((x) => x.role === 'assistant').at(-1);
      if (at) at.proposals = [...(at.proposals ?? []), prop];
    }
    return list;
  }, [data]);
  const decided = data?.proposals.filter((p) => p.decided_by_name) ?? [];
  return (
    <Dialog title={data?.chat.title ?? t('aia.conversation')} size="lg" onClose={onClose}>
      {!data ? (
        <Skeleton h={240} r="var(--radius-md)" />
      ) : (
        <div className="ai-act-transcript">
          <p className="muted small">
            {data.chat.person_name ?? '–'} · {agentName(data.chat.agent)}
          </p>
          {turns.map((x) =>
            x.role === 'user' ? (
              <div key={x.id} className="ai-q">
                {x.text}
                <time className="ai-act-at" dateTime={x.created_at}>
                  {dateTime(x.created_at!)}
                </time>
              </div>
            ) : (
              <Answer key={x.id} turn={x} readOnly />
            ),
          )}
          {decided.length ? (
            <ul className="ai-act-decided">
              {decided.map((p) => (
                <li key={p.id}>
                  <Icon name={p.state === 'applied' ? 'checkCircle' : 'x'} size={14} />{' '}
                  {t(p.state === 'applied' ? 'aia.approved_by' : 'aia.dismissed_by', { name: p.decided_by_name!, at: dateTime(p.decided_at!) })}: {p.summary}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
